import approved from './professionalTranslationOverrides.json' with { type: 'json' };

// Patient-public API supplies only activated, professionally approved text.
// This volatile cache is never persisted on the device and has no reviewer data.
const live = new Map();
const requests = new Map();
const activeVersions = new Map();
const normalizeCode = language => String(language || '').toLowerCase().split('-')[0];

function flatToNested(flat) {
  const nested = {};
  for (const [path, text] of Object.entries(flat || {})) {
    if (typeof text !== 'string' || !text.trim()) continue;
    const tokens = path.split('.');
    let cursor = nested;
    tokens.forEach((key, index) => {
      if (index === tokens.length - 1) cursor[key] = text;
      else {
        if (!cursor[key] || typeof cursor[key] !== 'object')
          cursor[key] = /^\d+$/.test(tokens[index + 1]) ? [] : {};
        cursor = cursor[key];
      }
    });
  }
  return nested;
}

export function setLiveProfessionalTranslationRelease(locale, namespaces = {}) {
  const code = normalizeCode(locale);
  const clean = {};
  for (const [namespace, paths] of Object.entries(namespaces)) {
    if (!/^[A-Za-z0-9_-]+$/.test(namespace) || !paths || typeof paths !== 'object') continue;
    clean[namespace] = Object.fromEntries(Object.entries(paths).filter(
      ([path, text]) => /^[A-Za-z0-9_.:-]+$/.test(path) && typeof text === 'string' && text.trim()));
  }
  live.set(code, clean);
}

export function professionalTranslationFor(language, namespace, path) {
  const code = normalizeCode(language);
  const released = live.get(code)?.[namespace]?.[path];
  if (typeof released === 'string' && released.trim()) return released;
  const release = approved.locales?.[code];
  if (!release?.approvedByStudio) return null;
  const value = path.split('.').reduce((node, part) => node?.[part], release.namespaces?.[namespace]);
  return typeof value === 'string' && value.trim() ? value : null;
}

export function installProfessionalTranslationOverrides(i18n, language, namespace) {
  const code = normalizeCode(language);
  const release = approved.locales?.[code];
  const content = release?.approvedByStudio && release.namespaces?.[namespace];
  if (content && i18n?.addResourceBundle && namespace !== 'journeyExplanation')
    i18n.addResourceBundle(code, namespace, content, true, true);
  const current = live.get(code)?.[namespace];
  if (current && i18n?.addResourceBundle && namespace !== 'journeyExplanation')
    i18n.addResourceBundle(code, namespace, flatToNested(current), true, true);
}

export async function loadPublishedProfessionalTranslations(i18n, language, force = false) {
  const code = normalizeCode(language);
  if (!['ar','bn','es','fa','fr','gu','it','pa','pl','pt','ro','so','tl','tr','ur','zh'].includes(code))
    return false;
  const current = requests.get(code);
  if (current && !force && Date.now() - current.at < 30000) return current.promise;
  const promise = (async () => {
    const res = await fetch('/api/professional-translations?localeCode=' + encodeURIComponent(code),
      { credentials: 'same-origin', cache: 'no-store' });
    if (!res.ok) throw new Error('Published translation service is not responding');
    const data = await res.json();
    if (!data?.ok || data.localeCode !== code || !data.namespaces) throw new Error('Invalid professional translation response');
    const nextVersions = JSON.stringify(data.releases || {});
    const lastVersions = activeVersions.get(code);
    activeVersions.set(code, nextVersions);
    // i18next has no automatic removal for old, deeply merged live keys.
    // An activation or rollback must recreate the original machine bundle
    // before applying the new professional snapshot.
    if (lastVersions !== undefined && lastVersions !== nextVersions
      && normalizeCode(i18n.language) === code
      && typeof window !== 'undefined' && window.location?.reload) {
      window.location.reload();
      return true;
    }
    setLiveProfessionalTranslationRelease(code, data.namespaces);
    for (const namespace of Object.keys(data.namespaces)) {
      if (i18n.hasResourceBundle?.(code, namespace))
        installProfessionalTranslationOverrides(i18n, code, namespace);
    }
    if (normalizeCode(i18n.language) === code) i18n.emit?.('languageChanged', i18n.language);
    return true;
  })().catch(() => false);
  requests.set(code, { at: Date.now(), promise });
  return promise;
}

export function overrideJourneyExplanation(language, journeyId, stageId, base) {
  const code = normalizeCode(language);
  const release = approved.locales?.[code];
  const override = release?.approvedByStudio && release.namespaces?.journeyExplanation?.[journeyId]?.[stageId];
  const flat = live.get(code)?.journeyExplanation || {};
  const prefix = journeyId + '.' + stageId + '.';
  const remote = flatToNested(Object.fromEntries(Object.entries(flat)
    .filter(([path]) => path.startsWith(prefix)).map(([path, text]) => [path.slice(prefix.length), text])));
  if (!override && !Object.keys(remote).length) return base;
  const merged = { ...(override || {}), ...remote };
  const steps = (base?.nextSteps || []).map(step => ({
    ...step, ...(override?.nextSteps?.[step.id] || {}), ...(remote.nextSteps?.[step.id] || {}),
  }));
  return { ...(base || {}), ...merged, nextSteps: steps };
}
